import type {
  AgendaItem,
  ApproverType,
  Meeting,
  ReviewStatus,
} from "@repo/types";
import {
  BaseExportConverters,
  BaseTableExportService,
  ExportContext,
  type FieldConverter,
} from "./baseTableExportService";
import { tenantStore } from "@/stores/tenantStore";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { meetingRepo } from "@/repositories/meetingRepo";
import { firstValueFrom } from "rxjs";

const baseExportConverters = new BaseExportConverters();

export class AgendaItemsExportService extends BaseTableExportService<AgendaItem> {
  public constructor() {
    super({
      id: {
        alias: "id",
        columns: {
          value: "id",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "ID",
      },
      name: {
        alias: "name",
        columns: {
          value: "name",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Name",
      },
      pending: {
        alias: "pending",
        columns: {
          value: "review.status",
        },
        fieldConverter: pendingConverter,
        resultFieldname: "Pending",
      },
      tags: {
        alias: "tags",
        columns: {
          value: "tags",
        },
        fieldConverter: baseExportConverters.tagsConverter,
        resultFieldname: "Tags",
      },
      description: {
        alias: "description",
        columns: {
          value: "description",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Description",
      },
      status: {
        alias: "status",
        columns: {
          value: "status",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Status",
      },
      createdAt: {
        alias: "createdAt",
        columns: {
          value: "createdAt",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Created",
      },
      createdBy: {
        alias: "createdBy",
        columns: {
          value: "createdBy",
        },
        fieldConverter: baseExportConverters.userConverter,
        resultFieldname: "Created By",
      },
      updatedAt: {
        alias: "updatedAt",
        columns: {
          value: "updatedAt",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Last Modified",
      },
      updatedBy: {
        alias: "updatedBy",
        columns: {
          value: "updatedBy",
        },
        fieldConverter: baseExportConverters.userConverter,
        resultFieldname: "Modified By",
      },
      teamId: {
        alias: "teamId",
        columns: {
          value: "teamId",
        },
        fieldConverter: baseExportConverters.teamConverter,
        resultFieldname: "Team",
      },
      requestedApprovalDate: {
        alias: "requestedApprovalDate",
        columns: {
          value: "requestedApprovalDate",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Req. Approval Date",
      },
      docapprovaltype: {
        alias: "docapprovaltype",
        columns: {
          value: "docapprovaltype",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Document Approval Type",
      },
      approver: {
        alias: "approver",
        columns: {
          value: "approver",
        },
        fieldConverter: baseExportConverters.approverConverter,
        resultFieldname: "Approver",
      },
      reviewStatus: {
        alias: "reviewStatus",
        columns: {
          value: "review.status",
          approverType: "approver.approverType",
        },
        fieldConverter: reviewStatusConverter,
        resultFieldname: "Review Status",
      },
      expirationDate: {
        alias: "expirationDate",
        columns: {
          value: "expirationDate",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Expiration Date",
      },
      price: {
        alias: "price",
        columns: {
          value: "price",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Price This Year",
      },
      annualPrice: {
        alias: "annualPrice",
        columns: {
          value: "annualPrice",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Annual Cost",
      },
      term: {
        alias: "term",
        columns: {
          value: "term",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Term in Months",
      },
      glAccount: {
        alias: "glAccount",
        columns: {
          value: "glAccount",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "GL Account",
      },
      lease: {
        alias: "lease",
        columns: {
          value: "lease",
        },
        fieldConverter: baseExportConverters.booleanToYesNoConverter,
        resultFieldname: "GASB 87",
      },
      software: {
        alias: "software",
        columns: {
          values: "software",
        },
        fieldConverter: baseExportConverters.booleanToYesNoConverter,
        resultFieldname: "GASB 96",
      },
      usedBy: {
        alias: "usedBy",
        columns: {
          value: "parentIds",
        },
        fieldConverter: sectionUsedInConverter,
        resultFieldname: "Agenda Sections Used In",
      },
      documents: {
        alias: "documents",
        columns: {
          value: "documentIds",
        },
        fieldConverter: documentsConverter,
        resultFieldname: "Documents",
      },
    });
  }
}

//#region Converters

const reviewStatusConverter: FieldConverter = async (values: {
  value?: ReviewStatus;
  approverType?: ApproverType;
}) => {
  const approverType = values.approverType;
  const hasApprover = !!approverType && approverType !== "none";
  if (!hasApprover) {
    return "N/A";
  }
  const reviewStatus = values.value;
  return reviewStatus || "Pending";
};

const sectionUsedInConverter: FieldConverter = async (
  values,
  context: ExportContext
): Promise<string> => {
  const sectionIds = values.value as string[] | undefined;
  if (!sectionIds || sectionIds.length === 0) {
    return "";
  }
  const tenantId = tenantStore.getState().id;
  const sections = await Promise.all(
    sectionIds.map((id) => context.getAgendaModule(tenantId, id))
  );
  return sections
    .filter((section) => section !== undefined)
    .map((section) => section?.name || "Unknown")
    .join("\n");
};

const documentsConverter: FieldConverter = async (
  values,
  context: ExportContext
): Promise<string> => {
  const documentIds = values.value as string[] | undefined;
  if (!documentIds || documentIds.length === 0) {
    return "";
  }
  const tenantId = tenantStore.getState().id;
  const documents = await Promise.all(
    documentIds.map((id) => context.getDocument(tenantId, id))
  );
  return documents
    .filter((doc) => doc !== undefined)
    .map((doc) => doc?.name || "Unknown")
    .join("\n");
};

const pendingConverter: FieldConverter = async (
  _values,
  context: ExportContext,
  object
): Promise<string> => {
  const state = context.getCustomState<{ meetings?: Meeting[] }>("pending");

  const tenantId = tenantStore.getState().id;
  if (!state.meetings) {
    state.meetings = await firstValueFrom(meetingRepo.list(tenantId));
  }

  const pending = agendaItemRepo.isPending(
    object as AgendaItem,
    state.meetings
  );

  return pending ? "Yes" : "No";
};

//#endregion

export const agendaItemsExportService = new AgendaItemsExportService();
